/**
 * Authorization Module
 *
 * Handles feature flag checking and permission management.
 * Uses the AccessControl library for RBAC permissions, backed by the
 * Checkpoint server's role/permission grants.
 *
 * Feature flags are served entirely by the Checkpoint server
 * (`checkpoint_feature_flags` / `checkpoint_user_features`). Flagsmith is
 * no longer used and has been removed.
 *
 * Both primary (SSO) users and secondary (multi-tenant) users are
 * supported. Secondary-user role/permissions are scoped per application
 * (`checkpoint_secondary_user_apps`) and arrive with the session — see
 * {@link CheckpointAuthz.setAuthContext}. Callers should not read
 * `user.apps[...]` directly; go through this module so permission logic
 * lives in one place for every app.
 *
 * @package @appfactory/checkpoint-client
 * @version 2.0.0
 */

import { AccessControl } from 'accesscontrol';
import { HttpClient } from './http-client';
import {
  AuthorizationProfile,
  PermissionResult,
  FeatureResult,
  PermissionCheckOptions,
  CheckpointConfig,
} from './types';

/**
 * Grant definition for AccessControl
 */
interface GrantDefinition {
  role: string;
  resource: string;
  action: string;
  attributes: string;
}

/**
 * Per-application grant for a secondary user, as delivered on
 * `SecondaryUser.apps[appName]` at login (see `secondary-users.ts`).
 */
interface SecondaryAppGrant {
  role?: string | null;
  permissions?: string[] | null;
}

/**
 * Minimal shape this module needs from the authenticated user object.
 * Deliberately loose (not importing `SecondaryUser`) so this module has
 * no dependency on the secondary-users module.
 */
interface AuthContextUser {
  apps?: Record<string, SecondaryAppGrant | undefined>;
  [key: string]: unknown;
}

/**
 * Resolved authorization context for the current session, set via
 * {@link CheckpointAuthz.setAuthContext}.
 */
interface AuthContext {
  userType: 'normal' | 'secondary';
  /** Secondary user's role for the running app (null if none assigned). */
  role: string | null;
  /** Secondary user's explicit permission grants for the running app. */
  permissions: string[];
}

/**
 * Authorization module for Checkpoint
 *
 * Provides access to:
 * - Feature flags via the Checkpoint server
 * - Access control via the AccessControl library (https://github.com/onury/accesscontrol)
 * - User roles and permissions, for both primary and secondary users
 *
 * @example
 * ```typescript
 * const authz = new CheckpointAuthz(httpClient, config);
 *
 * // Initialize (call once after authentication)
 * await authz.initialize();
 *
 * // Secondary-user sessions: call once the session is known (ClientHandler
 * // does this automatically for apps using it).
 * authz.setAuthContext('secondary', config.appName, sessionUser);
 *
 * // Check if a feature is enabled
 * const canUseExport = await authz.isFeatureEnabled('premium_export');
 *
 * // Check permission via AccessControl (works for primary and secondary)
 * const canEdit = await authz.can('update', 'article');
 * ```
 */
export class CheckpointAuthz {
  private http: HttpClient;
  private config: CheckpointConfig;
  private cachedProfile: AuthorizationProfile | null = null;
  private cacheTtl: number = 60000; // 1 minute
  private lastFetch: number = 0;

  // AccessControl instance
  private ac: AccessControl | null = null;
  private acInitialized = false;
  private userRoles: string[] = [];

  // Secondary-user context, set via setAuthContext()
  private authContext: AuthContext | null = null;

  constructor(http: HttpClient, config: CheckpointConfig) {
    this.http = http;
    this.config = config;
  }

  /**
   * Initialize the authorization module
   *
   * This should be called after the user is authenticated.
   * It fetches the AccessControl grants from the server.
   */
  async initialize(): Promise<void> {
    await this.initializeAccessControl();
  }

  /**
   * Record the current session's auth context so permission/role checks
   * work correctly for secondary users, whose roles are not stored in the
   * primary user table and cannot be resolved by the server's
   * `/authz/*` endpoints (those only know about primary/SSO users).
   *
   * For secondary users, the role/permissions come straight from the
   * `apps[appName]` grant already delivered at login — no extra network
   * call is needed. `ClientHandler` calls this automatically once the
   * session is known; call it manually only if you're not using
   * `ClientHandler`.
   *
   * @param userType 'normal' (primary/SSO) or 'secondary'
   * @param appName This application's Checkpoint app name (`config.appName`)
   * @param user The authenticated user object from the session
   */
  setAuthContext(userType: 'normal' | 'secondary', appName: string, user: AuthContextUser | null | undefined): void {
    if (userType !== 'secondary' || !user) {
      this.authContext = userType === 'secondary' ? { userType, role: null, permissions: [] } : null;
      if (userType === 'normal') {
        // Primary users are resolved server-side as before; drop any stale
        // secondary context and force a fresh profile/role fetch.
        this.clearCache();
      }
      return;
    }

    const grant = user.apps?.[appName];
    this.authContext = {
      userType: 'secondary',
      role: grant?.role ?? null,
      permissions: Array.isArray(grant?.permissions) ? (grant!.permissions as string[]) : [],
    };
    this.userRoles = this.authContext.role ? [this.authContext.role] : [];
    this.clearCache();
  }

  /**
   * Clear the secondary-user auth context (e.g. on logout).
   */
  clearAuthContext(): void {
    this.authContext = null;
  }

  /**
   * Initialize AccessControl with grants from server
   *
   * The grant table itself (`role` → `action`/`resource`) is global and
   * shared by every user type; only the *current user's roles* differ by
   * user type, which is why the server's accompanying `roles` field is
   * ignored here for secondary users (see {@link setAuthContext}).
   */
  private async initializeAccessControl(): Promise<void> {
    if (this.acInitialized) return;

    try {
      const response = await this.http.get<{ grants: GrantDefinition[]; roles: string[] }>(
        '/authz/accesscontrol/grants',
        true
      );

      this.ac = new AccessControl(response.grants);
      if (!this.authContext || this.authContext.userType !== 'secondary') {
        this.userRoles = response.roles;
      }
      this.acInitialized = true;
    } catch (error) {
      // AccessControl initialization failed, will fall back to server-side permission checks
      console.warn('AccessControl initialization failed, falling back to server-side permission checks');
    }
  }

  /**
   * Get the authorization profile for the current user
   *
   * For secondary users this is synthesized locally from the session's
   * app grant (`tier` is always `'secondary'`); the server profile
   * endpoint only resolves primary users.
   *
   * @param forceRefresh Force a refresh from server (ignored for secondary users)
   * @returns Authorization profile
   */
  async getProfile(forceRefresh = false): Promise<AuthorizationProfile> {
    if (this.authContext?.userType === 'secondary') {
      return {
        user_id: 0,
        tier: 'secondary',
        roles: this.authContext.role ? [this.authContext.role] : [],
        features: [],
        traits: {},
      };
    }

    if (!forceRefresh && this.cachedProfile && Date.now() - this.lastFetch < this.cacheTtl) {
      return this.cachedProfile;
    }

    this.cachedProfile = await this.http.get<AuthorizationProfile>('/authz/profile', true);
    this.lastFetch = Date.now();
    this.userRoles = this.cachedProfile.roles;

    return this.cachedProfile;
  }

  /**
   * Check if a feature is enabled
   *
   * Always resolved server-side (`checkpoint_feature_flags` /
   * `checkpoint_user_features`).
   *
   * @param featureKey Feature identifier
   * @param context Additional context for evaluation
   * @returns True if feature is enabled
   */
  async isFeatureEnabled(featureKey: string, context?: Record<string, unknown>): Promise<boolean> {
    const result = await this.http.post<FeatureResult>('/authz/feature', {
      feature: featureKey,
      context,
    });

    return result.enabled;
  }

  /**
   * Get all enabled features for the current user
   *
   * @returns Array of enabled feature keys
   */
  async getEnabledFeatures(): Promise<string[]> {
    const result = await this.http.get<{ features: string[] }>('/authz/features');
    return result.features;
  }

  /**
   * Check if user can perform an action on a resource using AccessControl
   *
   * For secondary users, an explicit entry in the user's own
   * `permissions` grant (`"action:resource"`, `"action:*"`, `"*:resource"`,
   * or `"*"`) is checked first, then the role-based AccessControl grants
   * using the user's app-scoped role. Primary users are checked purely via
   * AccessControl roles, falling back to the server when grants aren't
   * loaded.
   *
   * Actions follow CRUD pattern with ownership:
   * - create:own, create:any
   * - read:own, read:any
   * - update:own, update:any
   * - delete:own, delete:any
   *
   * @param action Action to perform (create, read, update, delete)
   * @param resource Resource type
   * @param options Check options including ownership context
   * @returns True if action is allowed
   */
  async can(
    action: string,
    resource: string,
    options?: PermissionCheckOptions
  ): Promise<boolean> {
    if (this.authContext?.userType === 'secondary') {
      if (this.hasExplicitPermission(action, resource)) {
        return true;
      }
      if (this.acInitialized && this.ac) {
        const isOwn = options?.context?.own === true;
        return this.checkPermission(action, isOwn ? 'Own' : 'Any', resource);
      }
      // No server fallback for secondary users: the server's /authz/check
      // endpoint only resolves primary-user roles and would misreport
      // access for a secondary user. Fail closed instead.
      return false;
    }

    if (this.acInitialized && this.ac) {
      try {
        const isOwn = options?.context?.own === true;
        const actionType = isOwn ? 'Own' : 'Any';
        return this.checkPermission(action, actionType, resource);
      } catch {
        // Fall back to server if AccessControl fails
      }
    }

    const result = await this.http.post<PermissionResult>('/authz/check', {
      action,
      resource,
      context: options?.context,
    }, true);

    return result.allowed;
  }

  /**
   * Check the secondary user's explicit permission grant
   * (`checkpoint_secondary_user_apps.permissions`) for an exact or
   * wildcard match, independent of role-based AccessControl grants.
   */
  private hasExplicitPermission(action: string, resource: string): boolean {
    const permissions = this.authContext?.permissions ?? [];
    if (permissions.length === 0) return false;

    const candidates = [
      '*',
      `${action}:*`,
      `*:${resource}`,
      `${action}:${resource}`,
    ];

    return permissions.some((p) => candidates.includes(p));
  }

  /**
   * Check permission using AccessControl
   */
  private checkPermission(action: string, ownership: 'Own' | 'Any', resource: string): boolean {
    if (!this.ac) return false;

    // Check each user role
    for (const role of this.userRoles) {
      let permission;
      const ac = this.ac.can(role);

      switch (action.toLowerCase()) {
        case 'create':
          permission = ownership === 'Own' ? ac.createOwn(resource) : ac.createAny(resource);
          break;
        case 'read':
          permission = ownership === 'Own' ? ac.readOwn(resource) : ac.readAny(resource);
          break;
        case 'update':
          permission = ownership === 'Own' ? ac.updateOwn(resource) : ac.updateAny(resource);
          break;
        case 'delete':
          permission = ownership === 'Own' ? ac.deleteOwn(resource) : ac.deleteAny(resource);
          break;
        default:
          // For custom actions, try as update
          permission = ownership === 'Own' ? ac.updateOwn(resource) : ac.updateAny(resource);
      }

      if (permission.granted) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check multiple permissions at once using AccessControl
   *
   * @param checks Array of permission checks
   * @returns Object with results for each check
   */
  async canMultiple(
    checks: Array<{
      action: string;
      resource: string;
      context?: Record<string, unknown>;
    }>
  ): Promise<Record<string, boolean>> {
    if (this.authContext?.userType === 'secondary') {
      const results: Record<string, boolean> = {};
      for (const check of checks) {
        const key = `${check.action}:${check.resource}`;
        results[key] = await this.can(check.action, check.resource, { context: check.context });
      }
      return results;
    }

    if (this.acInitialized && this.ac) {
      const results: Record<string, boolean> = {};
      for (const check of checks) {
        const key = `${check.action}:${check.resource}`;
        const isOwn = check.context?.own === true;
        results[key] = this.checkPermission(check.action, isOwn ? 'Own' : 'Any', check.resource);
      }
      return results;
    }

    // Fall back to server
    const result = await this.http.post<{ results: Record<string, boolean> }>(
      '/authz/check/multiple',
      { checks },
      true
    );

    return result.results;
  }

  /**
   * Get allowed attributes for an action on a resource
   *
   * @param action Action (create, read, update, delete)
   * @param resource Resource type
   * @param own Whether checking own resources
   * @returns Array of allowed attributes (* for all)
   */
  getPermittedAttributes(action: string, resource: string, own = false): string[] {
    if (!this.acInitialized || !this.ac) {
      return ['*']; // Default to all if not initialized
    }

    const ownership = own ? 'Own' : 'Any';

    for (const role of this.userRoles) {
      const ac = this.ac.can(role);
      let permission;

      switch (action.toLowerCase()) {
        case 'create':
          permission = ownership === 'Own' ? ac.createOwn(resource) : ac.createAny(resource);
          break;
        case 'read':
          permission = ownership === 'Own' ? ac.readOwn(resource) : ac.readAny(resource);
          break;
        case 'update':
          permission = ownership === 'Own' ? ac.updateOwn(resource) : ac.updateAny(resource);
          break;
        case 'delete':
          permission = ownership === 'Own' ? ac.deleteOwn(resource) : ac.deleteAny(resource);
          break;
        default:
          return ['*'];
      }

      if (permission.granted) {
        return permission.attributes;
      }
    }

    return [];
  }

  /**
   * Filter data by permitted attributes
   *
   * Uses AccessControl's filter method to remove non-permitted attributes.
   *
   * @param action Action being performed
   * @param resource Resource type
   * @param data Data to filter
   * @param own Whether this is own resource
   * @returns Filtered data
   */
  filterByPermission<T extends object>(
    action: string,
    resource: string,
    data: T,
    own = false
  ): Partial<T> {
    if (!this.acInitialized || !this.ac) {
      return data;
    }

    const ownership = own ? 'Own' : 'Any';

    for (const role of this.userRoles) {
      const ac = this.ac.can(role);
      let permission;

      switch (action.toLowerCase()) {
        case 'read':
          permission = ownership === 'Own' ? ac.readOwn(resource) : ac.readAny(resource);
          break;
        default:
          return data;
      }

      if (permission.granted) {
        return permission.filter(data) as Partial<T>;
      }
    }

    return {} as Partial<T>;
  }

  /**
   * Get user's roles
   *
   * @returns Array of role names (at most one for secondary users)
   */
  async getRoles(): Promise<string[]> {
    if (this.authContext?.userType === 'secondary') {
      return this.authContext.role ? [this.authContext.role] : [];
    }

    if (this.userRoles.length > 0) {
      return this.userRoles;
    }

    if (this.cachedProfile) {
      return this.cachedProfile.roles;
    }

    const result = await this.http.get<{ roles: string[] }>('/authz/roles', true);
    this.userRoles = result.roles;
    return result.roles;
  }

  /**
   * Check if user has a specific role
   *
   * @param role Role name
   * @returns True if user has the role
   */
  async hasRole(role: string): Promise<boolean> {
    const roles = await this.getRoles();
    return roles.includes(role);
  }

  /**
   * Check if user has any of the specified roles
   *
   * @param roles Role names to check
   * @returns True if user has any of the roles
   */
  async hasAnyRole(roles: string[]): Promise<boolean> {
    const userRoles = await this.getRoles();
    return roles.some((role) => userRoles.includes(role));
  }

  /**
   * Check if user has all of the specified roles
   *
   * @param roles Role names to check
   * @returns True if user has all of the roles
   */
  async hasAllRoles(roles: string[]): Promise<boolean> {
    const userRoles = await this.getRoles();
    return roles.every((role) => userRoles.includes(role));
  }

  /**
   * Get user's tier
   *
   * @returns User tier name ('secondary' for secondary users)
   */
  async getTier(): Promise<string> {
    const profile = await this.getProfile();
    return profile.tier;
  }

  /**
   * Clear the cache and reset initialized state
   */
  clearCache(): void {
    this.cachedProfile = null;
    this.lastFetch = 0;
  }

  /**
   * Reset the authorization module
   *
   * Call this when the user logs out.
   */
  async reset(): Promise<void> {
    this.acInitialized = false;
    this.ac = null;
    this.userRoles = [];
    this.authContext = null;
    this.cachedProfile = null;
    this.lastFetch = 0;
  }

  /**
   * Set cache TTL in milliseconds
   *
   * @param ttl Cache TTL
   */
  setCacheTtl(ttl: number): void {
    this.cacheTtl = ttl;
  }
}
